import { Pill, TooltipTrigger } from "@visiblethread/react-components";
import Flex, { GAP_CLASS } from "components/Flex";
import { idPropType } from "constants/app";
import useContainerSize from "hooks/useContainerSize";
import { get } from "lodash";
import { arrayOf, func, node, shape, string } from "prop-types";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

import classes from "./OverflowPillList.module.scss";

const getColGap = (ref) =>
  parseFloat(getComputedStyle(ref.current).columnGap) || 0;

const getColour = (item) => get(item, "color", "var(--color-middle-gray)");

const OverflowPillList = ({ items }) => {
  const { ref: containerRef, width: containerWidth } = useContainerSize({
    height: false,
  });
  const pillRefs = useRef([]);
  const morePillRef = useRef(null);
  const measureRef = useRef(null);
  const [measurements, setMeasurements] = useState({
    pillWidths: [],
    morePillWidth: 0,
    gap: 0,
  });

  useLayoutEffect(() => {
    pillRefs.current.length = items.length;
    const widths = pillRefs.current.map((el) =>
      Math.ceil(el?.getBoundingClientRect().width ?? 0)
    );
    const gap = getColGap(measureRef);

    setMeasurements({
      pillWidths: widths,
      morePillWidth: Math.ceil(
        morePillRef.current?.getBoundingClientRect().width ?? 0
      ),
      gap,
    });
  }, [items, containerWidth]);

  const { visibleCount, forcedSingle } = useMemo(() => {
    const { pillWidths, morePillWidth, gap } = measurements;
    if (!containerWidth || pillWidths.length === 0) {
      return { visibleCount: items.length, forcedSingle: false };
    }

    let used = 0;
    let count = 0;

    for (let i = 0; i < items.length; i++) {
      const w = pillWidths[i] ?? 0;
      const spaceBefore = count > 0 ? gap : 0;
      const isLast = i === items.length - 1;
      const moreReserve = isLast ? 0 : gap + morePillWidth;

      if (used + spaceBefore + w + moreReserve <= containerWidth) {
        used += spaceBefore + w;
        count++;
      } else {
        break;
      }
    }

    const forced = count === 0 && items.length > 0;
    return {
      visibleCount: Math.max(count, items.length > 0 ? 1 : 0),
      forcedSingle: forced,
    };
  }, [measurements, containerWidth, items.length]);

  const hiddenCount = items.length - visibleCount;
  const hiddenItems = items.slice(visibleCount);

  return (
    <div className={classes.root} data-testid="overflow-pill-list">
      <Flex
        gap={GAP_CLASS.GAP_200}
        ref={measureRef}
        className={classes.measure}
        aria-hidden="true"
      >
        {items.map((item, i) => (
          <div
            key={item.id || i}
            ref={(el) => {
              pillRefs.current[i] = el;
            }}
          >
            <Pill
              leftIcon={item.leftIcon}
              label={item.label}
              color={getColour(item)}
              onClose={item.onClose}
            />
          </div>
        ))}
        {items.length > 0 && (
          <div ref={morePillRef}>
            {/* Always measure with items.length to reserve the maximum possible width */}
            <Pill label={`+${items.length} more`} color={getColour(items[0])} />
          </div>
        )}
      </Flex>

      <Flex
        ref={containerRef}
        className={classes.container}
        gap={GAP_CLASS.GAP_200}
      >
        {items.slice(0, visibleCount).map((item, i) => (
          <div
            key={item.id || i}
            className={forcedSingle ? classes.pillItemForced : classes.pillItem}
            data-testid={`pill-item-${item.label}`}
          >
            <Pill
              leftIcon={item.leftIcon}
              label={item.label}
              color={getColour(item)}
              onClose={item.onClose}
            />
          </div>
        ))}
        {hiddenCount > 0 && (
          <div className={classes.morePill}>
            <TooltipTrigger
              content={
                <div className={classes.tooltip}>
                  {hiddenItems.map((item, i) => (
                    <span key={item.id || i}>{item.label}</span>
                  ))}
                </div>
              }
              delay={100}
              maxWidth="400px"
              maxHeight="400px"
            >
              <Pill
                label={`+${hiddenCount} more`}
                color={getColour(items[0])}
              />
            </TooltipTrigger>
          </div>
        )}
      </Flex>
    </div>
  );
};

OverflowPillList.propTypes = {
  items: arrayOf(
    shape({
      id: idPropType,
      label: string.isRequired,
      color: string,
      leftIcon: node,
      onClose: func,
    })
  ).isRequired,
};

export default OverflowPillList;
